/**
 * PathwayExplorer.jsx — iter821 (Feb 2026)
 *
 * "🗺️ Мои активирани пътища" panel — the missing SINGLE-FLOW view of
 *   Conditions → KEGG Pathways → Diseases → Foods & Interventions.
 *
 * Users click one of their diagnosed conditions above (or a BDI card
 * below) and see the exact chain of biological reasoning that connects
 * their diagnoses to breast-tissue relevance AND to the specific food /
 * lifestyle levers that support each pathway.
 *
 * Data source: GET /api/bhi/disease-intelligence/pathway-explorer
 *
 * Educational — pathway-based (KEGG). NOT diagnostic.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Map, ChevronDown, ChevronRight, Loader2, Apple, Activity, Target, Route,
  Stethoscope, FlaskConical, ChefHat, Sparkles, Info,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useBdiHighlight } from "@/utils/bdiHighlight";
import { useHealthEvent } from "@/utils/healthEvents";
import PathwayMenuModal from "./PathwayMenuModal";
import { getPathwayMetaphor, toneCls } from "@/utils/pathwayMetaphors";

const T = {
  title:            "🗺️ Мои активирани пътища",
  subtitle:         `Съгласно Вашите диагнози и избраните рискови фактори (от „Твоят път към превенция") тези биологични пътища са активни. За всеки виждате кои болести го използват и какви храни/интервенции го модулират.`,
  loading:          "Зареждане на pathway матрица…",
  empty:            `Нямате структурирани диагнози, нито избрани рискови фактори. Добавете диагнози от Profile → Health Conditions или отидете на „Твоят път към превенция", за да видите Вашата pathway карта.`,
  errorGeneric:     "Неуспешно зареждане.",
  top_foods:        "Топ храни (по брой пътища, които подкрепят)",
  top_interv:       "Топ интервенции",
  top_prev:         "Топ теми на профилактика",
  n_pathways:       "активирани пътища",
  n_conditions:     "диагнози",
  n_top_foods:      "топ храни",
  driven_by:        "Активиран от диагнози",
  driven_by_bhi:    "Активиран от твоите избрани фактори (BHI)",
  touches_diseases: "Засяга болести",
  modifiables:      "Фактори, на които можеш да въздействаш",
  foods:            "Храни",
  interventions:    "Интервенции",
  prevention:       "Профилактика",
  clickHint:        "натиснете за детайли",
  legalNote:        "Образователно · KEGG-базиран модел · НЕ е диагностично средство. Обсъдете с лекар.",
};

export default function PathwayExplorer({ isDark = false }) {
  const { API, user } = useAuth();
  const { hoveredCondition, setHoveredCondition, setHoveredDiseaseId } = useBdiHighlight();

  const [data, setData]       = useState(null);
  const [vaultDrivers, setVaultDrivers] = useState({});  // iter1009 — Gap #6
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [openPw, setOpenPw]   = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true); setErr(null);
    try {
      // iter1009 — Gap #6: fetch BHI PathwayExplorer AND Pathway Vault in parallel.
      const [expRes, vaultRes] = await Promise.all([
        axios.get(`${API}/bhi/disease-intelligence/pathway-explorer`),
        axios.get(`${API}/pathway-vault/mine`).catch(() => ({ data: { vault: null } })),
      ]);
      setData(expRes.data);
      // Build a lookup keyed by pathway_id and semantic name so we can
      // annotate PathwayExplorer rows with unified driver-source badges.
      const map = {};
      const vault = vaultRes.data?.vault;
      (vault?.active_pathways || []).forEach((row) => {
        const drivers = row.driving_conditions || [];
        const record = {
          weight:              row.weight || drivers.length,
          priority:            row.priority,
          kegg:                row.kegg,
          confirmed:           drivers.filter((d) => d.source === "user_confirmed"),
          from_lab:            drivers.filter((d) => d.source === "auto_from_lab"),
          from_bhi:            drivers.filter((d) => d.source === "auto_from_bhi"),
          from_dkg:            drivers.filter((d) => d.source === "auto_from_dkg"),
          from_genetics:       drivers.filter((d) => d.source === "auto_from_genetics"),
        };
        if (row.pathway_id) map[row.pathway_id] = record;
        if (row.kegg)       map[row.kegg]       = record;
      });
      setVaultDrivers(map);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || T.errorGeneric);
    } finally {
      setLoading(false);
    }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);
  useHealthEvent("conditions-changed", () => { load(); });

  // Filter pathway list by the currently hovered condition-chip.
  const filteredPathways = useMemo(() => {
    if (!data?.pathways) return [];
    if (!hoveredCondition) return data.pathways;
    return data.pathways.filter(
      (p) => (p.driving_conditions || []).includes(hoveredCondition),
    );
  }, [data?.pathways, hoveredCondition]);

  if (!user) return null;

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  const empty = data && (data.activated_pathways || []).length === 0;

  return (
    <section
      className={`${cardCls} p-5 mb-4`}
      data-testid="pathway-explorer"
    >
      <header className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center shrink-0">
          <Map className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold bg-gradient-to-r from-cyan-500 to-emerald-500 bg-clip-text text-transparent">
            {T.title}
          </h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {T.subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-xs px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-700 dark:text-slate-200 hover:opacity-80"
          data-testid="pathway-explorer-toggle"
        >
          {collapsed ? "Разгъни" : "Сгъни"}
        </button>
      </header>

      {collapsed ? null : (
        <>
          {loading && (
            <div className="py-8 flex items-center justify-center opacity-70" data-testid="pathway-explorer-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">{T.loading}</span>
            </div>
          )}

          {err && (
            <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="pathway-explorer-error">
              {err}
            </div>
          )}

          {!loading && !err && empty && (
            <div className={`p-4 rounded-lg text-sm ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-700"}`} data-testid="pathway-explorer-empty">
              {T.empty}
            </div>
          )}

          {!loading && !err && !empty && data && (
            <>
              {/* iter990 — BHI summary block (absorbed from former PathwayScoresPanel) */}
              {data.bhi_summary?.input?.total_active_selections > 0 && (
                <div className={`mb-4 rounded-xl border p-3 ${
                  isDark ? "bg-slate-900/40 border-slate-700" : "bg-slate-50 border-slate-200"
                }`} data-testid="pathway-explorer-bhi-summary">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? "bg-slate-800 text-slate-200" : "bg-white text-slate-700 border border-slate-200"
                    }`}>
                      Активни фактори: <b>{data.bhi_summary.input.total_active_selections}</b>
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                    }`}>
                      На които можеш да въздействаш: <b>{data.bhi_summary.input.modifiable_count}</b>
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-50 text-violet-700"
                    }`}>
                      От твоя профил: <b>{data.bhi_summary.input.reference_saved_count + data.bhi_summary.input.reference_auto_count}</b>
                    </span>
                  </div>
                  <div className={`p-2 rounded-lg text-[11px] leading-relaxed ${
                    isDark ? "bg-slate-800/60 text-slate-300" : "bg-white text-slate-600 border border-slate-100"
                  }`}>
                    <div className="font-semibold mb-1 flex items-center gap-1"><Info className="w-3 h-3" /> Как да четеш резултатите:</div>
                    <ul className="space-y-0.5">
                      <li>• <b>Числото до процеса</b> (0–1) = колко силно е активен: 0.75+ висока (червено), 0.5+ средно висока (жълто), 0.25+ средна (синьо), под 0.25 = ниска (зелено).</li>
                      <li>• <b className="text-emerald-500">Зелени скоби</b> = защитни фактори, които намаляват риска.</li>
                      <li>• <b className="text-emerald-500">●</b> = фактор, върху който можеш да въздействаш. <b className="text-violet-500">○</b> = от твоя профил (не се променя).</li>
                    </ul>
                  </div>

                  {data.bhi_summary.top_modifiable_factors?.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mb-1.5 text-emerald-700 dark:text-emerald-300">
                        <Target className="w-3 h-3" /> Твоите топ лостове за подобрение
                      </div>
                      <div className="space-y-1">
                        {data.bhi_summary.top_modifiable_factors.map((f, i) => (
                          <div key={f.factor_id} className="flex items-center gap-2 text-xs"
                               data-testid={`pathway-explorer-top-mod-${f.factor_id}`}>
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              isDark ? "bg-slate-800 text-slate-200" : "bg-white text-slate-700 border border-slate-200"
                            }`}>{i + 1}</span>
                            <span className="flex-1 truncate">{f.name_bg}</span>
                            <span className={`font-mono ${isDark ? "text-slate-400" : "text-slate-500"}`}
                                  title="Общ принос към биологичните процеси">
                              {(f.total_contribution || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2.5">
                        <Link
                          to="/dashboard/bhi/btre"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-500 hover:text-emerald-600 hover:underline"
                          data-testid="pathway-explorer-btre-link"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Отвори моя план за действие →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Summary stat strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="pathway-explorer-stats">
                <Stat label={T.n_pathways}   value={data.activated_pathways?.length || 0} accent="cyan" isDark={isDark} />
                <Stat label={T.n_conditions} value={data.user_conditions?.length || 0}   accent="pink" isDark={isDark} />
                <Stat label="BHI фактори"    value={
                  (data.pathways || []).reduce(
                    (acc, p) => acc + ((p.driving_bhi_factors || []).length ? 1 : 0), 0
                  )
                } accent="emerald" isDark={isDark} icon={Target} />
                <Stat label={T.top_interv}   value={data.top_interventions?.length || 0} accent="amber" isDark={isDark} icon={Activity} />
              </div>

              {/* Cross-pathway aggregated top lists */}
              <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-[11px] font-bold uppercase tracking-widest opacity-60">
                  Топ действия през всички пътища
                </div>
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  disabled={(data.top_foods || []).length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  data-testid="pathway-menu-generate-btn"
                  title={`Генерирай 7-дневно меню от Вашите ${(data.top_foods || []).length} pathway-подкрепени храни`}
                >
                  <ChefHat className="w-3.5 h-3.5" />
                  Генерирай 7-дневно меню от тези храни
                </button>
              </div>
              <div className="grid md:grid-cols-3 gap-3 mb-4">
                <AggregateBlock
                  title={T.top_foods}
                  items={data.top_foods}
                  icon={Apple}
                  accent="emerald"
                  isDark={isDark}
                  testid="pathway-top-foods"
                />
                <AggregateBlock
                  title={T.top_interv}
                  items={data.top_interventions}
                  icon={Activity}
                  accent="amber"
                  isDark={isDark}
                  testid="pathway-top-interventions"
                />
                <AggregateBlock
                  title={T.top_prev}
                  items={data.top_prevention_focus}
                  icon={Target}
                  accent="fuchsia"
                  isDark={isDark}
                  testid="pathway-top-prevention"
                />
              </div>

              {/* Per-pathway drill-down */}
              <div className="space-y-1.5" data-testid="pathway-explorer-list">
                {hoveredCondition && (
                  <div className={`text-[11px] italic mb-2 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                    Филтрирано за: <b>{hoveredCondition}</b> ({filteredPathways.length} пътища)
                  </div>
                )}
                {filteredPathways.map((p) => (
                  <PathwayRow
                    key={p.id}
                    pw={p}
                    vault={vaultDrivers[p.id] || vaultDrivers[p.kegg]}
                    isOpen={openPw === p.id}
                    onToggle={() => setOpenPw(openPw === p.id ? null : p.id)}
                    onHoverCondition={setHoveredCondition}
                    onHoverDisease={setHoveredDiseaseId}
                    isDark={isDark}
                  />
                ))}
              </div>

              <p className={`mt-3 text-[10px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                {T.legalNote}
              </p>
            </>
          )}
        </>
      )}

      <PathwayMenuModal
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        isDark={isDark}
      />
    </section>
  );
}

// ─────────────── Sub-components ───────────────

const ACCENT_MAP = {
  cyan:    { bg: "bg-cyan-100 dark:bg-cyan-500/20",       text: "text-cyan-700 dark:text-cyan-300",       ring: "ring-cyan-400" },
  emerald: { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-400" },
  amber:   { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300",     ring: "ring-amber-400" },
  pink:    { bg: "bg-pink-100 dark:bg-pink-500/20",       text: "text-pink-700 dark:text-pink-300",       ring: "ring-pink-400" },
  fuchsia: { bg: "bg-fuchsia-100 dark:bg-fuchsia-500/20", text: "text-fuchsia-700 dark:text-fuchsia-300", ring: "ring-fuchsia-400" },
};

function Stat({ label, value, accent = "cyan", isDark, icon: Icon }) {
  const s = ACCENT_MAP[accent] || ACCENT_MAP.cyan;
  return (
    <div className={`rounded-xl p-3 flex items-center gap-2 ${s.bg}`}>
      {Icon && <Icon className={`w-4 h-4 ${s.text}`} />}
      <div>
        <div className={`text-[10px] font-bold uppercase tracking-widest ${s.text}`}>{label}</div>
        <div className={`text-lg font-extrabold ${s.text}`}>{value}</div>
      </div>
    </div>
  );
}

function AggregateBlock({ title, items, icon: Icon, accent, isDark, testid }) {
  const s = ACCENT_MAP[accent] || ACCENT_MAP.cyan;
  return (
    <div
      className={`rounded-xl border p-3 ${isDark ? "bg-slate-900/60 border-slate-700" : "bg-white border-slate-200"}`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wide ${s.text}`}>
        <Icon className="w-4 h-4" />
        {title}
      </div>
      <div className="flex flex-wrap gap-1">
        {(items || []).slice(0, 6).map((it, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${s.bg} ${s.text}`}
            title={`Подкрепен от ${it.pathway_support} пътищ${it.pathway_support === 1 ? "" : "а"}`}
          >
            {it.item}
            <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-white/40 dark:bg-black/30 text-[10px] font-bold">
              {it.pathway_support}
            </span>
          </span>
        ))}
        {(items || []).length === 0 && (
          <span className="text-xs opacity-50 italic">Няма данни.</span>
        )}
      </div>
    </div>
  );
}

function PathwayRow({ pw, vault, isOpen, onToggle, onHoverCondition, onHoverDisease, isDark }) {
  const rowCls = `rounded-lg border transition-all ${
    isDark ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900/70"
           : "border-slate-200 bg-white hover:bg-slate-50"
  }`;
  const meta = getPathwayMetaphor(pw.id);
  // iter1009 — Gap #6 unified driver badges. Rendered inline in the row.
  const _driverBadges = [];
  if (vault) {
    if ((vault.confirmed || []).length)     _driverBadges.push({ key: "confirmed",  n: vault.confirmed.length,     icon: "✅", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300", title: "Потвърдени диагнози" });
    if ((vault.from_lab || []).length)      _driverBadges.push({ key: "lab",        n: vault.from_lab.length,      icon: "🧪", cls: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",             title: "Автоматично от лабораторни" });
    if ((vault.from_bhi || []).length)      _driverBadges.push({ key: "bhi",        n: vault.from_bhi.length,      icon: "🟣", cls: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",  title: "BHI рискови фактори" });
    if ((vault.from_dkg || []).length)      _driverBadges.push({ key: "dkg",        n: vault.from_dkg.length,      icon: "🩸", cls: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",          title: "DKG наследствен риск" });
    if ((vault.from_genetics || []).length) _driverBadges.push({ key: "genetics",   n: vault.from_genetics.length, icon: "🧬", cls: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300", title: "Герминална мутация" });
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className={rowCls}
      data-testid={`pathway-row-${pw.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-2 p-2.5"
        data-testid={`pathway-row-toggle-${pw.id}`}
      >
        {isOpen ? <ChevronDown className="w-3.5 h-3.5 opacity-60 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60 shrink-0" />}
        <span
          className={`w-7 h-7 rounded-full flex items-center justify-center text-base shrink-0 ${toneCls(meta.tone)}`}
          title={meta.short}
          aria-label={meta.short}
          data-testid={`pathway-emoji-${pw.id}`}
        >
          {meta.emoji}
        </span>
        <span className="text-sm font-semibold flex-1 truncate">{pw.name_bg}</span>
        {pw.kegg && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {pw.kegg}
          </span>
        )}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-300">
          {(pw.driving_conditions || []).length} диагн.
        </span>
        {(pw.driving_bhi_factors || []).length > 0 && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
            title="Активиран от твоите избрани рискови фактори (BHI)"
          >
            {(pw.driving_bhi_factors || []).length} {(pw.driving_bhi_factors || []).length === 1 ? "фактор" : "фактора"}
          </span>
        )}
        {typeof pw.bhi_risk_score === "number" && pw.bhi_risk_score > 0 && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 font-mono"
            title="BHI ниво на активност (0–1) — колко силно е активен процесът"
          >
            BHI {pw.bhi_risk_score.toFixed(2)}
          </span>
        )}
        {/* iter1013 — total driver-weight badge (only when Vault has it). */}
        {vault ? (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
              (vault.weight || 0) >= 3
                ? "bg-rose-100 text-rose-700 dark:bg-rose-500/25 dark:text-rose-200"
                : (vault.weight || 0) === 2
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                  : "bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300"
            }`}
            title={`Общ вес: ${vault.weight || 0} независим(и) източник(а)`}
            data-testid={`pathway-weight-${pw.id}`}
          >
            сила {vault.weight || 0}
          </span>
        ) : null}
        {/* iter1009 — Gap #6 unified driver badges (from Vault) */}
        {_driverBadges.map((b) => (
          <span
            key={b.key}
            className={`text-[10px] px-1.5 py-0.5 rounded ${b.cls}`}
            title={b.title}
            data-testid={`pathway-driver-${b.key}-${pw.id}`}
          >
            {b.icon} {b.n}
          </span>
        ))}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300">
          {(pw.diseases_touched || []).length} карти
        </span>
      </button>

      {isOpen && (
        <div className="px-3 pb-3 pt-1 space-y-2.5">
          {/* Metaphor sub-title for patient clarity */}
          <div className={`text-[11px] italic ${isDark ? "text-slate-400" : "text-slate-500"}`} data-testid={`pathway-metaphor-${pw.id}`}>
            {meta.emoji} {meta.short}
          </div>
          {/* Driving conditions row */}
          {(pw.driving_conditions || []).length > 0 && (
            <ChipList
              label={T.driven_by}
              icon={Stethoscope}
              items={pw.driving_conditions}
              accent="pink"
              isDark={isDark}
              onHover={(cid) => onHoverCondition?.(cid)}
              onLeave={() => onHoverCondition?.(null)}
              testid={`pathway-conditions-${pw.id}`}
            />
          )}

          {/* BHI-driving factors */}
          {(pw.driving_bhi_factors || []).length > 0 && (
            <div data-testid={`pathway-bhi-factors-${pw.id}`}>
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider mb-1 text-emerald-700 dark:text-emerald-300">
                <Target className="w-3 h-3" />
                {T.driven_by_bhi}
              </div>
              <div className="flex flex-wrap gap-1">
                {pw.driving_bhi_factors.map((f, i) => (
                  <span
                    key={f.factor_id + i}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${
                      f.contribution < 0
                        ? "bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-300"
                        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                    }`}
                    title={`Ниво: ${f.level_key} · принос ${f.contribution.toFixed(2)}`}
                  >
                    {f.modifiable ? "●" : "○"} {f.name_bg}
                    <span className="font-mono opacity-70">
                      {f.contribution < 0 ? "−" : "+"}{Math.abs(f.contribution).toFixed(2)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Touched diseases */}
          {(pw.diseases_touched || []).length > 0 && (
            <ChipList
              label={T.touches_diseases}
              icon={FlaskConical}
              items={(pw.diseases_touched || []).map((d) => ({ id: d.disease_id, label: d.name_bg }))}
              accent="fuchsia"
              isDark={isDark}
              onHover={(did) => onHoverDisease?.(did)}
              onLeave={() => onHoverDisease?.(null)}
              testid={`pathway-diseases-${pw.id}`}
            />
          )}

          {/* Modifiable factors */}
          {(pw.modifiable_factors || []).length > 0 && (
            <FactorList label={T.modifiables} items={pw.modifiable_factors} accent="amber" isDark={isDark} />
          )}

          {/* Foods */}
          {(pw.foods || []).length > 0 && (
            <FactorList label={T.foods} items={pw.foods} accent="emerald" isDark={isDark} icon={Apple} />
          )}

          {/* Interventions */}
          {(pw.interventions || []).length > 0 && (
            <FactorList label={T.interventions} items={pw.interventions} accent="cyan" isDark={isDark} icon={Activity} />
          )}

          {/* Prevention focus */}
          {(pw.prevention_focus || []).length > 0 && (
            <FactorList label={T.prevention} items={pw.prevention_focus} accent="fuchsia" isDark={isDark} icon={Target} />
          )}
        </div>
      )}
    </motion.div>
  );
}

function ChipList({ label, icon: Icon, items, accent, isDark, onHover, onLeave, testid }) {
  const s = ACCENT_MAP[accent] || ACCENT_MAP.cyan;
  return (
    <div data-testid={testid}>
      <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider mb-1 ${s.text}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => {
          const isObj = typeof it === "object" && it !== null;
          const id    = isObj ? it.id    : it;
          const lab   = isObj ? it.label : it;
          return (
            <button
              key={id + i}
              type="button"
              onMouseEnter={() => onHover?.(id)}
              onMouseLeave={() => onLeave?.()}
              onFocus={() => onHover?.(id)}
              onBlur={() => onLeave?.()}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ring-1 ${s.bg} ${s.text} hover:ring-2 hover:${s.ring} transition`}
            >
              {lab}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FactorList({ label, items, accent, isDark, icon: Icon }) {
  const s = ACCENT_MAP[accent] || ACCENT_MAP.cyan;
  return (
    <div>
      <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider mb-1 ${s.text}`}>
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span
            key={i}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${s.bg} ${s.text}`}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
